/**
 * demoScene.ts
 *
 * The hand-off between the DEMO PLAYER and the 3D house (card D8b, plan §4.7's
 * "expanded scene": *a button that goes full-screen into the 3D house with the
 * player floating; a cut's `viewpoint` flies the camera there*).
 *
 * Why a store and not a route query: the player lives in Settings → Demo, the
 * house is a dashboard route that takes a dashboard id, and the two surfaces
 * mount at different times (the layout mounts AFTER the navigation). A flag the
 * page sets and the layout consumes is the only shape that survives that
 * ordering without either surface reaching into the other's setup.
 *
 * The viewpoint is a ONE-SHOT message, not state: `takeViewpoint()` empties it,
 * so a reload (or a second dashboard opened later) never flies a camera to a
 * cut that happened in another session. Nothing here writes a viewpoint or a
 * camera pose — the layout's own `flyToViewpoint` is the only applier.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';

export const useDemoSceneStore = defineStore('demoScene', () => {
  /** The floating player is wanted on the next 3D surface that mounts. */
  const playerOpen = ref(false);
  /** The viewpoint the cut asked for (consumed once, then empty). */
  const pendingViewpoint = ref<string | null>(null);
  /** When the hand-off was made — a layout that mounts much later ignores it. */
  const requestedAt = ref(0);

  /** Open the scene: float the player (and fly to `viewpoint` when a cut names one). */
  function openScene(viewpoint: string | null = null): void {
    playerOpen.value = true;
    pendingViewpoint.value = viewpoint && viewpoint.trim() !== '' ? viewpoint.trim() : null;
    requestedAt.value = Date.now();
  }

  /** The player was closed (Esc, its own ✕, the settings page leaving). */
  function closeScene(): void {
    playerOpen.value = false;
    pendingViewpoint.value = null;
  }

  /** The layout's one read: the viewpoint, then the slot is empty again. */
  function takeViewpoint(): string | null {
    const viewpoint = pendingViewpoint.value;
    pendingViewpoint.value = null;
    return viewpoint;
  }

  /** A cut was previewed: remember its viewpoint for the next scene open. */
  function askViewpoint(viewpoint: string | null): void {
    pendingViewpoint.value = viewpoint && viewpoint.trim() !== '' ? viewpoint.trim() : null;
  }

  return { playerOpen, pendingViewpoint, requestedAt, openScene, closeScene, takeViewpoint, askViewpoint };
});
